import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { callsAPI } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { ScrollArea } from "../components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import {
  Phone, PhoneIncoming, Radio, Loader2, ArrowLeft,
  Clock, User, MessageSquare, ArrowRight, Headphones
} from "lucide-react";

function formatTime(iso) {
  return new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", second: "2-digit", hour12: true });
}

function timeSince(iso) {
  const seconds = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  return `${minutes}m ${seconds % 60}s`;
}

export default function LiveCallsPage() {
  const [liveCalls, setLiveCalls] = useState([]);
  const [selectedCall, setSelectedCall] = useState(null);
  const [transcript, setTranscript] = useState([]);
  const [loading, setLoading] = useState(true);
  const [callMeta, setCallMeta] = useState(null);
  const [listenDialogOpen, setListenDialogOpen] = useState(false);
  const [listenPhone, setListenPhone] = useState("");
  const [listenLoading, setListenLoading] = useState(false);
  const scrollRef = useRef(null);
  const navigate = useNavigate();

  const handleListenIn = async () => {
    if (!listenPhone || !selectedCall) { toast.error("Enter your phone number"); return; }
    setListenLoading(true);
    try {
      await callsAPI.listenIn(selectedCall, listenPhone);
      toast.success("Listen-in call initiated! Answer your phone to listen.");
      setListenDialogOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to initiate listen-in");
    } finally {
      setListenLoading(false);
    }
  };

  // Poll for live calls every 3 seconds
  useEffect(() => {
    const fetchLive = async () => {
      try {
        const res = await callsAPI.live();
        setLiveCalls(res.data);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    fetchLive();
    const interval = setInterval(fetchLive, 3000);
    return () => clearInterval(interval);
  }, []);

  // Poll transcript for selected call every 2 seconds
  useEffect(() => {
    if (!selectedCall) return;
    const fetchTranscript = async () => {
      try {
        const res = await callsAPI.liveTranscript(selectedCall);
        setTranscript(res.data.transcript || []);
        setCallMeta(res.data);
        // If call is no longer in progress, stop polling
        if (res.data.outcome !== "in_progress") {
          setLiveCalls(prev => prev.filter(c => c.id !== selectedCall && c.call_sid !== selectedCall));
        }
      } catch (err) { console.error(err); }
    };
    fetchTranscript();
    const interval = setInterval(fetchTranscript, 2000);
    return () => clearInterval(interval);
  }, [selectedCall]);

  // Auto-scroll transcript to bottom
  useEffect(() => {
    if (scrollRef.current) {
      const el = scrollRef.current.querySelector('[data-radix-scroll-area-viewport]');
      if (el) el.scrollTop = el.scrollHeight;
    }
  }, [transcript]);

  return (
    <div className="space-y-5 fade-in" data-testid="live-calls-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" className="text-slate-500 -ml-2 h-8" onClick={() => navigate("/dashboard")} data-testid="back-to-dashboard">
          <ArrowLeft className="w-3.5 h-3.5 mr-1.5" /> Dashboard
        </Button>
      </div>

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-slate-900 flex items-center gap-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Live Calls
            {liveCalls.length > 0 && (
              <span className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full bg-red-500 pulse-orange" />
                <Badge className="bg-red-50 text-red-600 border border-red-200 badge-pill text-[11px]">
                  {liveCalls.length} active
                </Badge>
              </span>
            )}
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">Real-time transcripts of ongoing calls</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Left: Active calls list */}
        <div className="space-y-3">
          <p className="text-xs text-slate-400 font-semibold uppercase tracking-widest">Active Calls</p>
          {loading ? (
            <div className="flex items-center justify-center h-32"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>
          ) : liveCalls.length === 0 ? (
            <Card className="border-slate-100 border-dashed">
              <CardContent className="py-12 text-center">
                <div className="w-12 h-12 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-3">
                  <Phone className="w-5 h-5 text-slate-400" />
                </div>
                <p className="text-sm text-slate-500 font-medium">No active calls right now</p>
                <p className="text-xs text-slate-400 mt-1">Live transcripts will appear here when a call comes in</p>
              </CardContent>
            </Card>
          ) : (
            liveCalls.map(call => (
              <Card
                key={call.id}
                className={`border-slate-100 cursor-pointer transition-all hover:shadow-md ${
                  selectedCall === call.id ? "border-primary ring-1 ring-primary/20" : ""
                }`}
                onClick={() => setSelectedCall(call.id)}
                data-testid={`live-call-${call.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center relative">
                      <PhoneIncoming className="w-5 h-5 text-red-500" />
                      <span className="absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full bg-red-500 border-2 border-white pulse-orange" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-mono font-medium text-slate-800 truncate">{call.caller_number}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <Badge className="bg-red-50 text-red-600 border border-red-200 badge-pill text-[9px]">
                          <Radio className="w-2.5 h-2.5 mr-1" /> Live
                        </Badge>
                        <span className="text-[10px] text-slate-400 flex items-center gap-1">
                          <Clock className="w-2.5 h-2.5" /> {timeSince(call.start_time)}
                        </span>
                      </div>
                    </div>
                    <ArrowRight className="w-4 h-4 text-slate-300" />
                  </div>
                  {call.transcript?.length > 0 && (
                    <p className="text-[11px] text-slate-400 mt-2 truncate">
                      Last: "{call.transcript[call.transcript.length - 1]?.text?.slice(0, 60)}..."
                    </p>
                  )}
                </CardContent>
              </Card>
            ))
          )}

          {/* Recent completed calls */}
          <p className="text-xs text-slate-400 font-semibold uppercase tracking-widest mt-6">Recent Calls</p>
          <Button variant="ghost" size="sm" className="w-full h-9 text-xs text-slate-500" onClick={() => navigate("/calls")}>
            View all calls <ArrowRight className="w-3 h-3 ml-1" />
          </Button>
        </div>

        {/* Right: Live transcript */}
        <div className="lg:col-span-2">
          {selectedCall ? (
            <Card className="border-slate-100" data-testid="live-transcript-panel">
              <CardHeader className="pb-3 px-5 pt-5 flex flex-row items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-red-50 flex items-center justify-center relative">
                    <MessageSquare className="w-4 h-4 text-red-500" />
                    <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-red-500 border-2 border-white pulse-orange" />
                  </div>
                  <div>
                    <CardTitle className="text-sm font-semibold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      Live Transcript
                    </CardTitle>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-[11px] font-mono text-slate-500">{callMeta?.caller_number}</span>
                      {callMeta?.outcome === "in_progress" ? (
                        <Badge className="bg-red-50 text-red-600 border border-red-200 badge-pill text-[9px]">
                          <Radio className="w-2.5 h-2.5 mr-1 animate-pulse" /> Streaming
                        </Badge>
                      ) : (
                        <Badge className="bg-slate-100 text-slate-500 badge-pill text-[9px]">
                          {callMeta?.outcome || "ended"}
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
                {callMeta?.start_time && (
                  <div className="flex items-center gap-2">
                    {callMeta?.outcome === "in_progress" && (
                      <Button variant="outline" size="sm" className="h-7 text-[11px] border-orange-200 text-orange-600 hover:bg-orange-50" onClick={() => setListenDialogOpen(true)} data-testid="listen-in-button">
                        <Headphones className="w-3 h-3 mr-1" /> Listen In
                      </Button>
                    )}
                    <span className="text-[11px] text-slate-400">Started {formatTime(callMeta.start_time)}</span>
                  </div>
                )}
              </CardHeader>
              <CardContent className="px-5 pb-5">
                <ScrollArea className="h-[500px] pr-2" ref={scrollRef}>
                  {transcript.length === 0 ? (
                    <div className="flex items-center justify-center h-48 text-slate-400">
                      <div className="text-center">
                        <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
                        <p className="text-sm">Waiting for conversation...</p>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {transcript.map((msg, i) => (
                        <div key={i} className={`flex ${msg.speaker === "ai" ? "justify-start" : "justify-end"}`} data-testid={`live-msg-${i}`}>
                          <div className={`max-w-[85%] px-4 py-2.5 text-[13px] leading-relaxed ${
                            msg.speaker === "ai"
                              ? "bg-slate-50 text-slate-700 rounded-2xl rounded-bl-md border border-slate-100"
                              : "bg-primary text-white rounded-2xl rounded-br-md shadow-sm shadow-primary/20"
                          }`}>
                            <p className={`text-[9px] uppercase tracking-widest mb-1 font-semibold ${
                              msg.speaker === "ai" ? "text-slate-400" : "text-white/60"
                            }`}>
                              {msg.speaker === "ai" ? "AI Agent" : "Customer"}
                            </p>
                            {msg.text}
                          </div>
                        </div>
                      ))}
                      {callMeta?.outcome === "in_progress" && (
                        <div className="flex justify-start">
                          <div className="px-4 py-3 rounded-2xl rounded-bl-md bg-slate-50 border border-slate-100">
                            <div className="flex gap-1">
                              <span className="w-2 h-2 rounded-full bg-slate-300 animate-bounce" style={{ animationDelay: "0ms" }} />
                              <span className="w-2 h-2 rounded-full bg-slate-300 animate-bounce" style={{ animationDelay: "150ms" }} />
                              <span className="w-2 h-2 rounded-full bg-slate-300 animate-bounce" style={{ animationDelay: "300ms" }} />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>
          ) : (
            <Card className="border-slate-100 border-dashed">
              <CardContent className="py-24 text-center">
                <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-4">
                  <MessageSquare className="w-7 h-7 text-slate-400" />
                </div>
                <p className="text-base font-semibold text-slate-700" style={{ fontFamily: 'Manrope, sans-serif' }}>Select a call to view live transcript</p>
                <p className="text-sm text-slate-400 mt-1">Click on an active call to see the conversation in real-time</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Listen-In Dialog */}
      <Dialog open={listenDialogOpen} onOpenChange={setListenDialogOpen}>
        <DialogContent data-testid="listen-in-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <Headphones className="w-5 h-5 text-orange-500" /> Listen In
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-500">Enter your phone number. We'll call you and connect you to the live call as a muted listener.</p>
          <Input
            placeholder="+1 (555) 123-4567"
            value={listenPhone}
            onChange={(e) => setListenPhone(e.target.value)}
            className="h-11"
            data-testid="listen-in-phone-input"
          />
          <DialogFooter>
            <Button variant="secondary" onClick={() => setListenDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleListenIn} disabled={listenLoading} className="bg-orange-500 hover:bg-orange-600" data-testid="listen-in-confirm">
              {listenLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Headphones className="w-3.5 h-3.5 mr-1.5" />}
              Call Me to Listen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
